/**
 * helpIntent — heuristique d'intention « requête ressemble-t-elle à une question
 * ou une requête à l'assistant ? » (HELP-DRAWER lot §4).
 *
 * Pilote la PRÉSÉLECTION et l'AFFICHAGE de l'entrée IA dans le champ unifié :
 * une question/requête présélectionne l'entrée IA (Entrée = IA) ; sinon, le
 * premier article est présélectionné et l'entrée IA n'apparaît pas (un indice
 * discret la remplace).
 *
 * Invariants (issus des retours PO) — l'IA ne doit JAMAIS être proposée quand
 * elle répondrait « je ne peux pas vous aider » :
 *   - **≥ 2 mots requis** : un mot seul ne suffit jamais (« client », « comment »…) ;
 *   - le **mot interrogatif doit INTRODUIRE** la requête (« comment créer un
 *     client » ✓) — pas traîner en fin de mots-clés (« client comment » ✗) ;
 *   - une **adresse à l'assistant** suffit : verbe de requête (décris, explique…),
 *     suffixe « -moi »/« -nous », ou 1ʳᵉ personne (« je… ») — couvre « Décris-moi… » ;
 *   - un **verbe d'action / de TÂCHE en tête** suffit aussi (« Créer une fiche
 *     client » = « comment créer une fiche client »). On utilise les INFINITIFS
 *     (créer, exporter, facturer…), distincts des noms (export, facture) : ainsi
 *     « facture client » (noms) reste lexical, mais « facturer un client » déclenche.
 *   - **un mot de CONTENU est requis** au-delà du verbe/interrogatif et des
 *     déterminants : « creer une » (verbe + article, sans objet) ou « comment
 *     créer » seuls = incomplets (l'IA répondrait « créer quoi ? ») → pas d'IA.
 *   - la simple LONGUEUR ne compte pas (une suite de noms reste une recherche).
 *
 * **Util autonome au drawer** (ce lot précède la palette globale SEARCH) — SEARCH
 * le RÉUTILISERA ensuite plutôt que d'introduire une dépendance inverse.
 */

import { foldAccents } from './helpSearch'

/**
 * Mots interrogatifs FR + EN. En minuscule, **accents conservés** : « où »
 * (interrogatif) doit rester distinct de « ou » (= « or », fréquent en recherche
 * lexicale, ex. « client ou prospect »).
 */
const INTERROGATIVES = new Set([
    // FR
    'comment', 'pourquoi', 'quand', 'où', 'qui', 'quoi', 'quel', 'quelle',
    'quels', 'quelles', 'combien', 'lequel', 'laquelle', 'lesquels', 'lesquelles',
    'est-ce', 'peut-on', 'puis-je', 'dois-je', 'faut-il',
    // EN
    'how', 'why', 'when', 'where', 'who', 'what', 'which', 'whom', 'whose',
    'can', 'could', 'should', 'does', 'do', 'is', 'are', 'will', 'would', 'may',
])

/**
 * Verbes EN TÊTE de requête qui signalent une intention (≠ suite de noms) :
 *   - verbes adressés à l'assistant (« décris-moi… ») ;
 *   - **verbes d'action / de tâche** (INFINITIFS FR, distincts des noms : « exporter »
 *     ≠ « export », « facturer » ≠ « facture », « gérer » ≠ « gestion ») — couvre
 *     « Créer une fiche client », « Modifier un tarif »…
 * On EXCLUT « liste/aide/guide » (recherches de fonctionnalités) et les EN
 * « export/import » (collisionnent avec les noms FR). Le « -moi » est capté à part.
 */
const LEADING_VERBS = new Set([
    // — Adressés à l'assistant (FR)
    'décris', 'décrivez', 'explique', 'expliquez', 'montre', 'montrez',
    'dis', 'dites', 'donne', 'donnez', 'résume', 'résumez', 'détaille',
    'détaillez', 'indique', 'indiquez', 'raconte', 'définis', 'définissez',
    'peux-tu', 'pouvez-vous', 'saurais-tu', 'sais-tu',
    // — Action / tâche (infinitifs FR)
    'créer', 'ajouter', 'modifier', 'éditer', 'changer', 'mettre', 'supprimer',
    'retirer', 'enlever', 'dupliquer', 'copier', 'renommer', 'déplacer',
    'transférer', 'fusionner', 'importer', 'exporter', 'télécharger', 'téléverser',
    'envoyer', 'générer', 'configurer', 'paramétrer', 'gérer', 'organiser',
    'classer', 'trier', 'filtrer', 'activer', 'désactiver', 'bloquer', 'débloquer',
    'inscrire', 'désinscrire', 'planifier', 'programmer', 'facturer', 'encaisser',
    'rembourser', 'valider', 'signer', 'émarger', 'attribuer', 'associer', 'lier',
    'rattacher', 'détacher', 'archiver', 'restaurer', 'anonymiser', 'consulter',
    'afficher', 'voir', 'masquer', 'imprimer', 'partager', 'inviter', 'accéder',
    'ouvrir', 'retrouver', 'saisir', 'remplir', 'sélectionner', 'choisir',
    'définir', 'personnaliser', 'calculer', 'suivre',
    // — EN (verbes d'action / requête ; hors « export/import » = noms FR)
    'describe', 'explain', 'show', 'tell', 'give', 'summarize', 'define', 'outline',
    'create', 'add', 'edit', 'update', 'modify', 'delete', 'remove', 'duplicate',
    'copy', 'rename', 'move', 'transfer', 'merge', 'configure', 'manage', 'enable',
    'disable', 'block', 'send', 'generate', 'download', 'upload', 'schedule',
    'invoice', 'validate', 'sign', 'assign', 'link', 'archive', 'restore',
    'anonymize', 'view', 'print', 'share', 'invite', 'enroll', 'find', 'access',
    'open', 'select', 'choose', 'customize',
])

/**
 * Version repliée (accents retirés) des verbes : la détection des verbes est
 * **insensible aux accents** (« creer » sans accent déclenche comme « créer ») —
 * contrairement aux interrogatifs, qui restent accent-sensibles (« où » ≠ « ou »).
 */
const LEADING_VERBS_FOLDED = new Set(Array.from(LEADING_VERBS, foldAccents))

/** Prépositions pouvant introduire une question (« à quelle heure », « de quoi »). */
const LEADING_PREPOSITIONS = new Set([
    'à', 'a', 'de', 'du', 'des', 'dans', 'en', 'pour', 'sur', 'par', 'avec', 'depuis',
])

/**
 * Mots-outils (déterminants, articles, prépositions, conjonctions, pronoms) qui ne
 * sont PAS du « contenu ». Servent à exiger un sujet/objet réel après le verbe ou
 * l'interrogatif (sinon « creer une » n'a pas d'objet). Les mots d'1 lettre (à, a,
 * y, l, d…) sont déjà exclus par la longueur.
 */
const STOP_WORDS = new Set([
    // FR
    'le', 'la', 'les', 'un', 'une', 'des', 'de', 'du', 'au', 'aux', 'ce', 'cet',
    'cette', 'ces', 'mon', 'ma', 'mes', 'ton', 'ta', 'tes', 'son', 'sa', 'ses',
    'notre', 'nos', 'votre', 'vos', 'leur', 'leurs', 'en', 'dans', 'sur', 'sous',
    'pour', 'par', 'avec', 'sans', 'vers', 'chez', 'depuis', 'et', 'ou', 'ni',
    'se', 'me', 'te', 'ne', 'pas', 'on', 'il', 'elle', 'ils', 'elles', 'lui',
    // EN
    'an', 'the', 'my', 'your', 'his', 'her', 'its', 'our', 'their', 'of', 'to',
    'in', 'on', 'for', 'with', 'and', 'this', 'that', 'these', 'those', 'some',
    'any', 'it', 'we', 'they', 'you',
])

function isFirstPerson(word: string): boolean {
    return word === 'je' || word === 'i' || word.startsWith("j'") || word.startsWith("i'")
}

/** Mot de « contenu » (sujet/objet) : ni mot-outil, ni interrogatif/verbe, ni adresse. */
function isContentWord(word: string): boolean {
    if (word.length < 2) return false
    if (STOP_WORDS.has(word) || INTERROGATIVES.has(word)) return false
    if (LEADING_VERBS_FOLDED.has(foldAccents(word))) return false
    if (word.endsWith('-moi') || word.endsWith('-nous')) return false
    if (isFirstPerson(word)) return false
    return true
}

/** Découpe en « mots » (minuscule, apostrophes normalisées, ponctuation de bord retirée). */
function toWords(query: string): string[] {
    return query
        .toLowerCase()
        .replace(/[‘’]/g, "'")
        .split(/\s+/)
        .map((t) => t.replace(/^[^\p{L}\p{N}'-]+|[^\p{L}\p{N}'-]+$/gu, ''))
        .filter(Boolean)
}

/** Le mot interrogatif INTRODUIT la requête (en tête, ou juste après une préposition). */
function isInterrogativeLed(words: string[]): boolean {
    if (INTERROGATIVES.has(words[0])) return true
    if (words.length >= 2 && LEADING_PREPOSITIONS.has(words[0]) && INTERROGATIVES.has(words[1])) {
        return true
    }
    return false
}

/**
 * Vrai si la requête est une question ou une requête/tâche adressée à l'assistant.
 * Insensible à la casse ; sensible aux accents pour « où » vs « ou ».
 */
export function isLikelyQuestion(query: string): boolean {
    const q = query.trim()
    if (!q) return false

    const words = toWords(q)
    // Un seul mot ne suffit jamais — l'IA répondrait qu'elle ne peut pas aider.
    if (words.length < 2) return false

    const first = words[0]
    const hasSignal =
        q.includes('?') ||
        isInterrogativeLed(words) ||
        // Verbe (requête à l'assistant OU action/tâche) en tête — accent-insensible.
        LEADING_VERBS_FOLDED.has(foldAccents(first)) ||
        // 1ʳᵉ personne (« je veux… », « j'aimerais… »).
        isFirstPerson(first) ||
        // Adresse explicite (« décris-MOI… », « montre-NOUS… »).
        words.some((tk) => tk.endsWith('-moi') || tk.endsWith('-nous'))
    if (!hasSignal) return false

    // …mais il faut un vrai SUJET/OBJET : « creer une » (verbe + article) ou
    // « comment créer » seuls sont incomplets → pas d'IA.
    return words.some(isContentWord)
}
